import React, { useState, useEffect } from 'react';
import { 
  AlertTriangle, Eye, CheckCircle, XCircle, Clock, RefreshCw, 
  Flag, FileText, MessageSquare, UserX, Download, User, Calendar, Star,
  Ban, Check, Shield, AlertCircle
} from 'lucide-react';
import toast from 'react-hot-toast';

/**
 * Admin Reports Page - Updated with Elevated User Reports filter
 * Allows administrators to view and handle all reported content across schools
 */
const ReportsPage = () => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    status: 'pending', // pending, approved, rejected, all
    school: '',
    reportType: 'all', // all, material, comment, user, professor_rating, elevated, flagged_materials
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
  });
  const [schools, setSchools] = useState([]);
  const [reportCounts, setReportCounts] = useState({
    all: 0,
    material: 0,
    comment: 0,
    user: 0,
    professor_rating: 0,
    elevated: 0, // NEW: Count for elevated user reports
    flagged_materials: 0 // NEW: Count for flagged materials (compliance violations)
  });
  const [resolveConfirmation, setResolveConfirmation] = useState(null);
  const [userAction, setUserAction] = useState('none');
  const [suspensionDays, setSuspensionDays] = useState(7);

  // Load reports from API
  useEffect(() => {
    const fetchReports = async () => {
      try {
        setLoading(true);
        
        // Build query parameters for filtering and pagination
        const params = new URLSearchParams({
          limit: pagination.limit,
          offset: (pagination.page - 1) * pagination.limit,
          ...(filters.school && { schoolId: filters.school }),
        });

        // Handle special filter types
        if (filters.reportType === 'elevated') {
          params.append('elevatedOnly', 'true');
        } else if (filters.reportType === 'flagged_materials') {
          params.append('flaggedMaterialsOnly', 'true');
        } else {
          params.append('status', filters.status);
          params.append('reportType', filters.reportType);
        }
        
        // Fetch reports using the correct endpoint
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/admin/reports?${params}`,
          {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`,
            },
          }
        );
        
        if (!response.ok) {
          throw new Error('Failed to fetch reports');
        }
        
        const data = await response.json();
        
        setReports(data.reports || []);
        setPagination(prev => ({
          ...prev,
          total: data.total || 0,
        }));
        
        // Also fetch schools for filtering if not already loaded
        if (schools.length === 0) {
          const schoolsResponse = await fetch(
            `${process.env.REACT_APP_API_URL}/api/schools`,
            {
              headers: {
                'Authorization': `Bearer ${localStorage.getItem('token')}`,
              },
            }
          );
          
          if (schoolsResponse.ok) {
            const schoolsData = await schoolsResponse.json();
            setSchools(schoolsData);
          }
        }
      } catch (err) {
        console.error('Error fetching reports:', err);
        setError(err.message);
        toast.error('Failed to load reports');
      } finally {
        setLoading(false);
      }
    };
    
    fetchReports();
    fetchReportCounts();
  }, [pagination.page, filters]);

  // Fetch report counts for each type
  const fetchReportCounts = async () => {
    try {
      const promises = [
        fetch(`${process.env.REACT_APP_API_URL}/api/admin/reports?status=pending&reportType=all&limit=0`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        }),
        fetch(`${process.env.REACT_APP_API_URL}/api/admin/reports?status=pending&reportType=material&limit=0`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        }),
        fetch(`${process.env.REACT_APP_API_URL}/api/admin/reports?status=pending&reportType=comment&limit=0`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        }),
        fetch(`${process.env.REACT_APP_API_URL}/api/admin/reports?status=pending&reportType=user&limit=0`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        }),
        fetch(`${process.env.REACT_APP_API_URL}/api/admin/reports?status=pending&reportType=professor_rating&limit=0`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        }),
        // NEW: Fetch elevated user reports count (pending only for badge)
        fetch(`${process.env.REACT_APP_API_URL}/api/admin/reports?elevatedOnly=true&status=pending&limit=0`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        }),
        // NEW: Fetch flagged materials count (pending only for badge)
        fetch(`${process.env.REACT_APP_API_URL}/api/admin/reports?flaggedMaterialsOnly=true&status=pending&limit=0`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        })
      ];

      const responses = await Promise.all(promises);
      const data = await Promise.all(responses.map(r => r.json()));

      setReportCounts({
        all: data[0].total || 0,
        material: data[1].total || 0,
        comment: data[2].total || 0,
        user: data[3].total || 0,
        professor_rating: data[4].total || 0,
        elevated: data[5].total || 0, // NEW: Elevated reports count
        flagged_materials: data[6].total || 0 // NEW: Flagged materials count
      });
    } catch (error) {
      console.error('Error fetching report counts:', error);
    }
  };
  
  // Handle filter changes
  const handleFilterChange = (key, value) => {
    console.log(`Changing filter ${key} to ${value}`);
    setFilters(prev => {
      const newFilters = {
        ...prev,
        [key]: value,
      };
      console.log('New filters:', newFilters);
      return newFilters;
    });
    
    // Reset to first page when filters change
    setPagination(prev => ({
      ...prev,
      page: 1,
    }));
  };
  
  // Handle pagination
  const handlePageChange = (newPage) => {
    setPagination(prev => ({
      ...prev,
      page: newPage,
    }));
  };
  
  // Handle report resolution (approve or reject)
  const handleResolveReport = async (reportId, resolution) => {
    try {
      const requestBody = { 
        action: resolution, 
        note: resolveConfirmation.note || '',
        userAction: resolveConfirmation.reportType === 'user' ? userAction : 'none',
        suspensionDays: parseInt(suspensionDays, 10) || 7
      };

      // Use the same endpoint - backend will detect material reviews by ID prefix
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/reports/${reportId}/resolve`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(requestBody)
        }
      );
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to resolve report');
      }
      
      // Update reports list
      const newStatus = resolution === 'approve' ? 'approved' : 'rejected';
      setReports(reports.map(report => 
        report.id === reportId 
          ? { ...report, status: newStatus, resolvedAt: new Date().toISOString() } 
          : report
      ));
      
      // Close confirmation dialog
      setResolveConfirmation(null);
      
      // Update counts
      fetchReportCounts();
      
      toast.success(`Report ${resolution === 'approve' ? 'approved' : 'rejected'} successfully`);
      setResolveConfirmation(null);
      setUserAction('none'); // Reset action after completion
      
    } catch (error) {
      console.error('Error resolving report:', error);
      toast.error(error.message || 'Failed to resolve report');
    }
  };
  
  // Calculate total pages
  const totalPages = Math.ceil(pagination.total / pagination.limit);
  
  // Format date for display
  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    return new Date(timestamp).toLocaleDateString(undefined, { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Get status badge
  const getStatusBadge = (status) => {
    switch(status) {
      case 'approved':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
            <CheckCircle size={12} className="mr-1" />
            Approved
          </span>
        );
      case 'rejected':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200">
            <XCircle size={12} className="mr-1" />
            Rejected
          </span>
        );
      case 'pending':
      default:
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200">
            <AlertTriangle size={12} className="mr-1" />
            Pending
          </span>
        );
    }
  };

  // Get report type info
  const getReportTypeInfo = (reportType) => {
    switch(reportType) {
      case 'material':
        return { icon: FileText, label: 'Material', color: 'text-blue-500' };
      case 'comment':
        return { icon: MessageSquare, label: 'Comment', color: 'text-green-500' };
      case 'user':
        return { icon: UserX, label: 'User', color: 'text-red-500' };
      case 'professor_rating':
        return { icon: Star, label: 'Professor Rating', color: 'text-yellow-500' };
      default:
        return { icon: Flag, label: 'Report', color: 'text-gray-500' };
    }
  };

  // Tab with badge component
  const TabButton = ({ type, label, icon: Icon, count, active, onClick, variant = 'default' }) => (
    <button
      onClick={onClick}
      className={`relative px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
        active 
          ? variant === 'elevated'
            ? 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200 border-2 border-purple-300 dark:border-purple-700'
            : variant === 'flagged'
            ? 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200 border-2 border-orange-300 dark:border-orange-700'
            : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
          : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600'
      }`}
    >
      <div className="flex items-center space-x-2">
        <Icon size={16} />
        <span>{label}</span>
        {count > 0 && (
          <span className={`inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none text-white rounded-full ${
            variant === 'elevated' ? 'bg-purple-500' : variant === 'flagged' ? 'bg-orange-500' : 'bg-red-500'
          }`}>
            {count}
          </span>
        )}
      </div>
    </button>
  );

  const getUserStatusBadge = (status) => {
    switch(status) {
      case 'suspended':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200">
            <Clock size={12} className="mr-1" />
            Suspended
          </span>
        );
      case 'banned':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200">
            <Ban size={12} className="mr-1" />
            Banned
          </span>
        );
      case 'active':
      default:
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
            <Check size={12} className="mr-1" />
            Active
          </span>
        );
    }
  };

  // NEW: Get role badge for elevated reports
  const getRoleBadge = (role) => {
    switch(role) {
      case 'admin':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200">
            <Shield size={12} className="mr-1" />
            Administrator
          </span>
        );
      case 'moderator':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
            <Shield size={12} className="mr-1" />
            Moderator
          </span>
        );
      default:
        return null;
    }
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Platform Reports
          </h1>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Review and manage reported content across all schools
          </p>
        </div>
        
        <button
          onClick={() => {
            setFilters({
              status: 'pending',
              school: '',
              reportType: 'all',
            });
            setPagination(prev => ({ ...prev, page: 1 }));
          }}
          className="flex items-center px-4 py-2 text-sm text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
        >
          <RefreshCw size={16} className="mr-2" />
          Reset Filters
        </button>
      </div>

      {/* Report Type Tabs */}
      <div className="mb-6">
        <div className="flex flex-wrap gap-2">
          <TabButton
            type="all"
            label="All Reports"
            icon={Flag}
            count={reportCounts.all}
            active={filters.reportType === 'all'}
            onClick={() => handleFilterChange('reportType', 'all')}
          />
          <TabButton
            type="material"
            label="Materials"
            icon={FileText}
            count={reportCounts.material}
            active={filters.reportType === 'material'}
            onClick={() => handleFilterChange('reportType', 'material')}
          />
          <TabButton
            type="comment"
            label="Comments"
            icon={MessageSquare}
            count={reportCounts.comment}
            active={filters.reportType === 'comment'}
            onClick={() => handleFilterChange('reportType', 'comment')}
          />
          <TabButton
            type="user"
            label="Users"
            icon={UserX}
            count={reportCounts.user}
            active={filters.reportType === 'user'}
            onClick={() => handleFilterChange('reportType', 'user')}
          />
          <TabButton
            type="professor_rating"
            label="Professor Ratings"
            icon={Star}
            count={reportCounts.professor_rating}
            active={filters.reportType === 'professor_rating'}
            onClick={() => handleFilterChange('reportType', 'professor_rating')}
          />
          {/* NEW: Elevated User Reports Tab */}
          <TabButton
            type="elevated"
            label="Elevated User Reports"
            icon={Shield}
            count={reportCounts.elevated}
            active={filters.reportType === 'elevated'}
            onClick={() => handleFilterChange('reportType', 'elevated')}
            variant="elevated"
          />
          {/* NEW: Flagged Materials Tab */}
          <TabButton
            type="flagged_materials"
            label="Flagged Materials"
            icon={AlertCircle}
            count={reportCounts.flagged_materials}
            active={filters.reportType === 'flagged_materials'}
            onClick={() => handleFilterChange('reportType', 'flagged_materials')}
            variant="flagged"
          />
        </div>
      </div>
      
      {/* Filters */}
      <div className="mb-6 bg-white dark:bg-gray-800 rounded-lg shadow p-4">
        <div className="flex flex-wrap gap-4">
          {/* Status filter - Hide when elevated or flagged filter is active */}
          {filters.reportType !== 'elevated' && filters.reportType !== 'flagged_materials' && (
            <div className="w-full sm:w-auto">
              <label htmlFor="status-filter" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Status
              </label>
              <select
                id="status-filter"
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-700 shadow-sm focus:border-red-500 focus:ring-red-500 dark:bg-gray-700 dark:text-white sm:text-sm"
              >
                <option value="pending">Pending</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
                <option value="all">All Reports</option>
              </select>
            </div>
          )}
          
          {/* School filter */}
          <div className="w-full sm:w-auto">
            <label htmlFor="school-filter" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              School
            </label>
            <select
              id="school-filter"
              value={filters.school}
              onChange={(e) => handleFilterChange('school', e.target.value)}
              className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-700 shadow-sm focus:border-red-500 focus:ring-red-500 dark:bg-gray-700 dark:text-white sm:text-sm"
            >
              <option value="">All Schools</option>
              {schools.map((school) => (
                <option key={school.id} value={school.id}>
                  {school.name}
                </option>
              ))}
            </select>
          </div>

          {/* NEW: Show elevated filter info */}
          {filters.reportType === 'elevated' && (
            <div className="w-full">
              <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-md p-3">
                <div className="flex items-center">
                  <Shield className="h-5 w-5 text-purple-500 mr-2" />
                  <span className="text-sm font-medium text-purple-800 dark:text-purple-200">
                    Showing reports about moderators and administrators only
                  </span>
                </div>
                <p className="text-xs text-purple-600 dark:text-purple-300 mt-1">
                  These reports require administrator-level attention and cannot be resolved by moderators.
                </p>
              </div>
            </div>
          )}

          {/* NEW: Show flagged materials filter info */}
          {filters.reportType === 'flagged_materials' && (
            <div className="w-full">
              <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-md p-3">
                <div className="flex items-center">
                  <AlertCircle className="h-5 w-5 text-orange-500 mr-2" />
                  <span className="text-sm font-medium text-orange-800 dark:text-orange-200">
                    Showing materials automatically flagged for academic integrity violations
                  </span>
                </div>
                <p className="text-xs text-orange-600 dark:text-orange-300 mt-1">
                  These materials were flagged by AI analysis for potential policy violations and require review.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
      
      {/* Reports list */}
      <div className="bg-white dark:bg-gray-800 shadow overflow-hidden rounded-lg">
        {loading ? (
          <div className="flex justify-center items-center p-8">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-red-500"></div>
          </div>
        ) : error ? (
          <div className="text-center p-8 text-red-500">
            {error}
          </div>
        ) : reports.length === 0 ? (
          <div className="text-center p-8 text-gray-500 dark:text-gray-400">
            <Flag className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-600 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              No reports found
            </h3>
            <p>
              {filters.reportType === 'elevated' 
                ? 'No elevated user reports found.' 
                : filters.reportType === 'flagged_materials'
                ? 'No flagged materials found. All materials are compliant! 🎉'
                : filters.status === 'pending' 
                  ? 'No pending reports found. 🎉' 
                  : `No ${filters.status !== 'all' ? filters.status : ''} ${filters.reportType !== 'all' ? filters.reportType : ''} reports found.`
              }
            </p>
          </div>
        ) : (
          <div className="space-y-6 p-4">
            {reports.map((report) => {
              const typeInfo = getReportTypeInfo(report.reportType);
              const Icon = typeInfo.icon;
              
              return (
                <div 
                  key={report.id} 
                  className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 border border-gray-200 dark:border-gray-600"
                >
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
                    <div>
                      <div className="flex items-center">
                        <Icon className={`h-5 w-5 ${typeInfo.color} mr-2`} />
                        <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                          {report.reportType === 'material' && (report.itemDetails?.title || 'Unknown Material')}
                          {report.reportType === 'comment' && 'Comment Report'}
                          {report.reportType === 'user' && `User: ${report.itemDetails?.name || 'Unknown User'}`}
                          {report.reportType === 'professor_rating' && `Professor Rating: ${report.itemDetails?.professorName || 'Unknown Professor'}`}
                        </h3>
                        {/* NEW: Show flagged badge for auto-flagged materials */}
                        {filters.reportType === 'flagged_materials' && report.reporter?.name === 'System' && (
                          <div className="ml-3">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200">
                              <AlertCircle size={12} className="mr-1" />
                              Automatically Flagged
                            </span>
                          </div>
                        )}
                        {/* NEW: Show role badge for elevated user reports */}
                        {report.reportType === 'user' && report.itemDetails?.role && (
                          <div className="ml-3">
                            {getRoleBadge(report.itemDetails.role)}
                          </div>
                        )}
                      </div>
                      <div className="flex items-center mt-1 text-sm text-gray-500 dark:text-gray-400">
                        <span className="mr-4">Type: {typeInfo.label}</span>
                        {report.reportType === 'material' && (
                          <span>Course: {report.itemDetails?.courseName || 'Unknown'}</span>
                        )}
                        {report.reportType === 'comment' && (
                          <span>On: {report.itemDetails?.materialTitle || 'Unknown Material'}</span>
                        )}
                        {report.reportType === 'user' && (
                          <span>Email: {report.itemDetails?.email || 'Unknown'}</span>
                        )}
                        {report.reportType === 'professor_rating' && (
                          <span>Rating: {report.itemDetails?.rating || 'N/A'}/5 by {report.itemDetails?.ratingAuthorName || 'Unknown'}</span>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-3">
                      {getStatusBadge(report.status)}
                      
                      {report.reportType === 'material' && report.itemDetails?.fileUrl && (
                        <a 
                          href={`${process.env.REACT_APP_API_URL}${report.itemDetails.fileUrl?.startsWith('/') ? '' : '/'}${report.itemDetails.fileUrl}`}
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="inline-flex items-center px-3 py-1 border border-gray-300 dark:border-gray-600 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700"
                        >
                          <Download className="h-4 w-4 mr-1" />
                          View Material
                        </a>
                      )}
                    </div>
                  </div>
                  
                  {/* Report details for different types */}
                  {/* NEW: Special display for flagged materials */}
                  {filters.reportType === 'flagged_materials' && report.reportType === 'material' && (
                    <div className="mb-4">
                      <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Compliance Violation Details:
                      </h4>
                      <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-md p-3">
                        <div className="text-sm text-orange-800 dark:text-orange-200">
                          <strong>Flagged for:</strong> {report.reason || 'Academic integrity policy violation'}
                        </div>
                        <div className="text-xs text-orange-600 dark:text-orange-300 mt-1">
                          This material was automatically flagged by AI analysis for potential violations of academic integrity policies.
                        </div>
                      </div>
                    </div>
                  )}

                  {report.reportType === 'comment' && (
                    <div className="mb-4">
                      <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Reported Content:
                      </h4>
                      <p className="text-sm text-gray-600 dark:text-gray-400 bg-white dark:bg-gray-800 p-3 rounded-md">
                        {report.itemDetails?.content || 'No content available'}
                      </p>
                    </div>
                  )}
                  
                  {report.reportType === 'professor_rating' && (
                    <div className="mb-4">
                      <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Reported Rating:
                      </h4>
                      <div className="bg-white dark:bg-gray-800 p-3 rounded-md">
                        <div className="flex items-center mb-2">
                          <div className="flex items-center mr-3">
                            {Array.from({ length: 5 }, (_, i) => (
                              <Star
                                key={i}
                                size={16}
                                className={`${
                                  i < (report.itemDetails?.rating || 0)
                                    ? 'text-yellow-500 fill-current'
                                    : 'text-gray-300 dark:text-gray-600'
                                }`}
                              />
                            ))}
                          </div>
                          <span className="text-sm font-medium text-gray-900 dark:text-white">
                            {report.itemDetails?.rating || 'N/A'}/5
                          </span>
                        </div>
                        {report.itemDetails?.review && (
                          <p className="text-sm text-gray-600 dark:text-gray-400">
                            "{report.itemDetails.review}"
                          </p>
                        )}
                      </div>
                    </div>
                  )}

                  {report.reportType === 'user' && (
                    <div className="mb-4">
                      <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        User Report History:
                      </h4>
                      <div className="bg-white dark:bg-gray-800 p-3 rounded-md">
                        <div className="flex items-center mb-2">
                          <span className="text-sm font-medium text-gray-900 dark:text-white mr-2">
                            Current Status:
                          </span>
                          {getUserStatusBadge(report.itemDetails?.status || 'active')}
                          {report.itemDetails?.status === 'suspended' && report.itemDetails?.statusUntil && (
                            <span className="text-xs text-gray-500 dark:text-gray-400 ml-2">
                              Until {formatDate(report.itemDetails.statusUntil)}
                            </span>
                          )}
                        </div>
                        <div className="mb-2">
                          <span className="text-sm font-medium text-gray-900 dark:text-white mr-2">
                            Total Reports:
                          </span>
                          <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300">
                            {report.itemDetails?.reportCount || 0}
                          </span>
                        </div>
                        {report.itemDetails?.reportCount > 0 && (
                          <div className="text-xs text-red-600 dark:text-red-400">
                            This user has been reported multiple times.
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  
                  <div className="mb-4">
                    <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Report Reason:
                    </h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400 bg-white dark:bg-gray-800 p-3 rounded-md">
                      {report.reason || 'No reason provided'}
                    </p>
                  </div>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Reported By:
                      </h4>
                      <div className="flex items-center">
                        <User className="h-4 w-4 text-gray-500 dark:text-gray-400 mr-2" />
                        <span className="text-sm text-gray-600 dark:text-gray-400">
                          {report.reporter?.name || 'Unknown User'}
                        </span>
                      </div>
                    </div>
                    
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Report Date:
                      </h4>
                      <div className="flex items-center">
                        <Calendar className="h-4 w-4 text-gray-500 dark:text-gray-400 mr-2" />
                        <span className="text-sm text-gray-600 dark:text-gray-400">
                          {formatDate(report.createdAt)}
                        </span>
                      </div>
                    </div>
                    
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        School:
                      </h4>
                      <span className="text-sm text-gray-600 dark:text-gray-400">
                        {report.school?.name || 'Unknown School'}
                      </span>
                    </div>
                  </div>
                  
                  {report.status !== 'pending' && (
                    <div className="mb-4">
                      <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Resolution:
                      </h4>
                      <div className="flex items-center">
                        <span className="text-sm text-gray-600 dark:text-gray-400 mr-2">
                          {report.status === 'approved' ? 
                            `${typeInfo.label} ${report.reportType === 'user' ? 'flagged' : 'removed'}` : 
                            `${typeInfo.label} kept`
                          } by {report.resolvedBy?.name || 'Unknown'} on {formatDate(report.resolvedAt)}
                        </span>
                      </div>
                      {report.moderatorNote && (
                        <div className="mt-2">
                          <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Note:
                          </h4>
                          <p className="text-sm text-gray-600 dark:text-gray-400 bg-white dark:bg-gray-800 p-3 rounded-md">
                            {report.moderatorNote}
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {report.status === 'pending' && (
                    <div className="flex justify-end space-x-3">
                      <button
                        onClick={() => setResolveConfirmation({ ...report, action: 'reject' })}
                        className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 shadow-sm text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700"
                      >
                        <XCircle className="mr-2 -ml-1 h-5 w-5 text-red-500" />
                        Reject
                      </button>
                      <button
                        onClick={() => setResolveConfirmation({ ...report, action: 'approve' })}
                        className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700"
                      >
                        <CheckCircle className="mr-2 -ml-1 h-5 w-5" />
                        Approve
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
        
        {/* Pagination - Same as before */}
        {!loading && reports.length > 0 && totalPages > 1 && (
          <div className="bg-white dark:bg-gray-800 px-4 py-3 flex items-center justify-between border-t border-gray-200 dark:border-gray-700 sm:px-6">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => handlePageChange(Math.max(1, pagination.page - 1))}
                disabled={pagination.page === 1}
                className={`relative inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md 
                  ${pagination.page === 1 
                    ? 'text-gray-400 dark:text-gray-500 bg-gray-100 dark:bg-gray-800 cursor-not-allowed' 
                    : 'text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600'}`
                }
              >
                Previous
              </button>
              <button
                onClick={() => handlePageChange(Math.min(totalPages, pagination.page + 1))}
                disabled={pagination.page === totalPages}
                className={`ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md 
                  ${pagination.page === totalPages 
                    ? 'text-gray-400 dark:text-gray-500 bg-gray-100 dark:bg-gray-800 cursor-not-allowed' 
                    : 'text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600'}`
                }
              >
                Next
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700 dark:text-gray-300">
                  Showing <span className="font-medium">{reports.length > 0 ? (pagination.page - 1) * pagination.limit + 1 : 0}</span> to{' '}
                  <span className="font-medium">
                    {Math.min(pagination.page * pagination.limit, pagination.total)}
                  </span>{' '}
                  of <span className="font-medium">{pagination.total}</span> results
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                  <button
                    onClick={() => handlePageChange(Math.max(1, pagination.page - 1))}
                    disabled={pagination.page === 1}
                    className={`relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 dark:border-gray-600 text-sm font-medium 
                      ${pagination.page === 1 
                        ? 'text-gray-400 dark:text-gray-500 bg-gray-100 dark:bg-gray-800 cursor-not-allowed' 
                        : 'text-gray-500 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600'}`
                    }
                  >
                    Previous
                  </button>
                  
                  <span className="relative inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm font-medium text-gray-700 dark:text-gray-200">
                    {pagination.page} of {totalPages}
                  </span>
                  
                  <button
                    onClick={() => handlePageChange(Math.min(totalPages, pagination.page + 1))}
                    disabled={pagination.page === totalPages}
                    className={`relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 dark:border-gray-600 text-sm font-medium 
                      ${pagination.page === totalPages 
                        ? 'text-gray-400 dark:text-gray-500 bg-gray-100 dark:bg-gray-800 cursor-not-allowed' 
                        : 'text-gray-500 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600'}`
                    }
                  >
                    Next
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Resolve Confirmation Modal - Same as before but with elevated user context */}
      {resolveConfirmation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full">
            <div className="flex items-center mb-4">
              <div className={`p-2 rounded-full ${
                resolveConfirmation.action === 'approve' 
                  ? 'bg-green-100 text-green-600 dark:bg-green-900 dark:text-green-400' 
                  : 'bg-red-100 text-red-600 dark:bg-red-900 dark:text-red-400'
              } mr-3`}>
                {resolveConfirmation.action === 'approve' 
                  ? <CheckCircle className="h-6 w-6" /> 
                  : <XCircle className="h-6 w-6" />
                }
              </div>
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                {resolveConfirmation.action === 'approve' ? 'Approve Report' : 'Reject Report'}
              </h3>
            </div>
            
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              {resolveConfirmation.action === 'approve' ? (
                resolveConfirmation.reportType === 'user' ?
                  `Approving this report will allow you to take action on the ${resolveConfirmation.itemDetails?.role || 'user'} account.` :
                  resolveConfirmation.reportType === 'professor_rating' ?
                    'Approving this report will remove the professor rating from the platform.' :
                    `Approving this report will remove the ${resolveConfirmation.reportType} from the platform.`
              ) : (
                `Rejecting this report will keep the ${resolveConfirmation.reportType} on the platform.`
              )}
            </p>

            {/* Show role warning for elevated users */}
            {resolveConfirmation.reportType === 'user' && 
             resolveConfirmation.itemDetails?.role && 
             ['moderator', 'admin'].includes(resolveConfirmation.itemDetails.role) && (
              <div className="mb-4 p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-md">
                <div className="flex items-center">
                  <Shield className="h-5 w-5 text-yellow-500 mr-2" />
                  <span className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
                    Elevated User Report
                  </span>
                </div>
                <p className="text-xs text-yellow-600 dark:text-yellow-300 mt-1">
                  You are reviewing a report about a {resolveConfirmation.itemDetails.role}. 
                  Please exercise extra caution when taking action.
                </p>
              </div>
            )}
            
            {/* User action section (only for user reports being approved) */}
            {resolveConfirmation.reportType === 'user' && resolveConfirmation.action === 'approve' && (
              <div className="mb-4 p-4 bg-gray-50 dark:bg-gray-700 rounded-md">
                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  User Action:
                </h4>
                <div className="space-y-3">
                  <div className="flex items-center">
                    <input
                      id="action-none"
                      type="radio"
                      name="user-action"
                      value="none"
                      checked={userAction === 'none'}
                      onChange={() => setUserAction('none')}
                      className="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 dark:border-gray-600"
                    />
                    <label htmlFor="action-none" className="ml-2 block text-sm text-gray-700 dark:text-gray-300">
                      No action (just flag the user)
                    </label>
                  </div>
                  
                  <div className="flex items-center">
                    <input
                      id="action-suspend"
                      type="radio"
                      name="user-action"
                      value="suspend"
                      checked={userAction === 'suspend'}
                      onChange={() => setUserAction('suspend')}
                      className="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 dark:border-gray-600"
                    />
                    <label htmlFor="action-suspend" className="ml-2 block text-sm text-gray-700 dark:text-gray-300">
                      Suspend user temporarily
                    </label>
                  </div>
                  
                  <div className="flex items-center">
                    <input
                      id="action-ban"
                      type="radio"
                      name="user-action"
                      value="ban"
                      checked={userAction === 'ban'}
                      onChange={() => setUserAction('ban')}
                      className="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 dark:border-gray-600"
                    />
                    <label htmlFor="action-ban" className="ml-2 block text-sm text-gray-700 dark:text-gray-300">
                      Ban user permanently
                    </label>
                  </div>
                  
                  {/* Suspension duration input (only shown when suspend is selected) */}
                  {userAction === 'suspend' && (
                    <div className="mt-3 pl-6">
                      <label htmlFor="suspension-days" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Suspension Duration (days):
                      </label>
                      <input
                        id="suspension-days"
                        type="number"
                        min="1"
                        max="365"
                        value={suspensionDays}
                        onChange={(e) => setSuspensionDays(Math.max(1, parseInt(e.target.value) || 1))}
                        className="mt-1 block w-24 rounded-md border-gray-300 dark:border-gray-700 shadow-sm focus:border-red-500 focus:ring-red-500 sm:text-sm dark:bg-gray-800 dark:text-white"
                      />
                    </div>
                  )}
                </div>
              </div>
            )}
            
            <div className="mb-4">
              <label htmlFor="resolution-note" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {resolveConfirmation.reportType === 'user' && (userAction === 'suspend' || userAction === 'ban') 
                  ? 'Reason for action (will be sent to user):' 
                  : 'Add a note (optional):'}
              </label>
              <textarea
                id="resolution-note"
                rows="3"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                placeholder={resolveConfirmation.reportType === 'user' && (userAction === 'suspend' || userAction === 'ban')
                  ? "Explain why this action is being taken..."
                  : "Add a note about your decision..."}
                value={resolveConfirmation.note || ''}
                onChange={(e) => setResolveConfirmation({ ...resolveConfirmation, note: e.target.value })}
                required={resolveConfirmation.reportType === 'user' && (userAction === 'suspend' || userAction === 'ban')}
              ></textarea>
              {resolveConfirmation.reportType === 'user' && (userAction === 'suspend' || userAction === 'ban') && (
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  This reason will be included in the notification email sent to the user.
                </p>
              )}
            </div>
            
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setResolveConfirmation(null)}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  // Check if a reason is required but not provided
                  if (resolveConfirmation.reportType === 'user' && 
                      (userAction === 'suspend' || userAction === 'ban') && 
                      !resolveConfirmation.note) {
                    toast.error("Please provide a reason for this action");
                    return;
                  }
                  
                  handleResolveReport(
                    resolveConfirmation.id, 
                    resolveConfirmation.action === 'approve' ? 'approve' : 'reject'
                  );
                }}
                className={`px-4 py-2 text-white rounded-md ${
                  resolveConfirmation.action === 'approve' 
                    ? 'bg-green-600 hover:bg-green-700' 
                    : 'bg-red-600 hover:bg-red-700'
                }`}
              >
                {resolveConfirmation.action === 'approve' ? 'Approve' : 'Reject'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReportsPage;